import { Anchor, Avatar, Box, Button, Group, Stack, Text, TextInput, Title } from "@mantine/core"
import { MagnifyingGlassIcon } from "@phosphor-icons/react"
import dayjs from "dayjs"
import React from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { Link, useNavigate } from "react-router"
import useSWR from "swr"
import { GovernanceConfigApi, NetworkApi, PricingApi } from "../../apis"
import { discountValueText, termOptions } from "../../components/buyer-sales/product-list/cart-model"
import { LIST_STORAGE_KEY } from "../../components/buyer-sales/product-list/utils"
import { SupplierSelect } from "../../components/buyer-sales/supplier-select"
import { DotPill } from "../../components/delivery/atoms"
import { PageLayout } from "../../components/layout"
import { usePermission } from "../../components/permission"
import { DeniedCard, ListState, ReasonTooltip } from "../../components/ui"
import { INetworkPlus } from "../../models/Network"
import { ICart } from "../../store/Cart"
import { currencyFormat } from "../../utils/currency"
import { renderBusinessName } from "../../utils/render-business-name"
import { renderName } from "../../utils/render-name"
import classes from "./list.module.css"

const SUPPLIER_KEY = `${LIST_STORAGE_KEY}.supplierId`

const readSupplier = (): string => {
  try {
    const raw = sessionStorage.getItem(SUPPLIER_KEY)
    return raw ? (JSON.parse(raw) as string) : ""
  } catch {
    return ""
  }
}

// The catalogue page (ProductList) reads this key on mount to pick its supplier.
const writeSupplier = (id: string) => {
  try {
    sessionStorage.setItem(SUPPLIER_KEY, JSON.stringify(id))
  } catch {
    /* ignore quota */
  }
}

/** BE `canOrder` (false while the network is inactive); older responses fall back to the status code. */
const canOrder = (record: INetworkPlus) =>
  (record as { canOrder?: boolean }).canOrder !== false && record?.networkStatus?.toUpperCase() !== "INACTIVE"

const LEGACY_ACTIVE_LABEL = "Идэвхтэй"

const hasStaff = (staff: any) => !!staff && Object.keys(staff).length > 0

type CardProps = {
  supplier: INetworkPlus
  selected: boolean
  cartLines: number
  cartAmount: number
  onOpenCatalog: () => void
  detailHref?: string
  catalogReason?: string
}

function Fact({ label, children, strong = false, mono = false }: Readonly<{ label: string; children: React.ReactNode; strong?: boolean; mono?: boolean }>) {
  return (
    <>
      <Text component="dt" className={classes.label}>
        {label}
      </Text>
      <Text component="dd" m={0} className={`${classes.value}${mono ? ` ${classes.mono}` : ""}`} data-strong={strong || undefined}>
        {children}
      </Text>
    </>
  )
}

function SupplierCard({ supplier, selected, cartLines, cartAmount, onOpenCatalog, detailHref, catalogReason }: Readonly<CardProps>) {
  const { t } = useTranslation(["order", "common"])
  const id = supplier.id as string
  const name = renderBusinessName(supplier, "-")

  // Same source as the cart: GET /ord/network/:id carries paymentTerms and their discounts.
  const { data: network } = useSWR(["buyer-customer.network", id], () => NetworkApi.get(id), { revalidateOnFocus: false })
  const today = dayjs().format("YYYY-MM-DD")
  const { data: dates } = useSWR(
    ["buyer-customer.delivery-dates", id, today],
    () => GovernanceConfigApi.availableDeliveryDates({ businessId: id, from: today, days: 14 }),
    { revalidateOnFocus: false, shouldRetryOnError: false },
  )

  // B27: the supplier's active promotions that reach this buyer (403 without an active network).
  const { data: promos } = useSWR(
    ["buyer-customer.promotions", id],
    () => PricingApi.buyerPromotions(id).catch(() => null),
    { revalidateOnFocus: false, shouldRetryOnError: false },
  )

  const options = termOptions(network)
  // Canvas shows the network's main term here; the full list is chosen at checkout.
  const termText = network?.paymentTerm?.description || options[0]?.description || "-"
  // Canvas: the main term's discount first («5%»), then the others «<term>-д 10%».
  const mainTermId = network?.paymentTerm?.id
  const discounts = options.filter((o) => o.discount?.discountValue)
  const mainDiscount = discounts.find((o) => o.id === mainTermId)
  const discountText =
    discounts.length > 0
      ? [
        mainDiscount ? discountValueText(mainDiscount.discount!) : null,
        ...discounts
          .filter((o) => o !== mainDiscount)
          .map((o) => t("order:buyerCustomer.pick.discountOther", { term: o.description, value: discountValueText(o.discount!) })),
      ].filter(Boolean).join(". ")
      : t("order:buyerCustomer.pick.none")
  const promoRows = promos?.rows || []
  const promoText = !promos
    ? "-"
    : promoRows.length === 0
      ? t("order:buyerCustomer.pick.none")
      : [promoRows.slice(0, 2).map((p) => p.name).join(", "), promoRows.length > 2 ? `+${promoRows.length - 2}` : ""].filter(Boolean).join(" ")
  const nextDate = dates?.dates?.find((d) => d.available)?.date || "-"

  const manager = hasStaff(supplier.receiverStaff) ? supplier.receiverStaff : null
  const managerText = manager ? [renderName(manager), manager.email].filter(Boolean).join(" · ") : "-"

  // POST /ord/network/list returns networkStatus as a Mongolian label; GET /ord/network/:id
  // returns the ACTIVE / INACTIVE code (logics/network/get.ts), so prefer the detail.
  const status = (network?.networkStatus || supplier.networkStatus)?.toUpperCase()
  // Until order-service B40 is deployed both endpoints send the legacy Mongolian label.
  const isActive = status === "ACTIVE" || (network?.canOrder ?? status === LEGACY_ACTIVE_LABEL.toUpperCase())
  const isPending = status === "PENDING"
  const statusLabel = isActive
    ? t("order:buyerCustomer.active")
    : isPending
      ? t("order:buyerCustomer.pending")
      : status === "INACTIVE"
        ? t("order:buyerCustomer.inactive")
        : supplier.networkStatus || "-"
  const statusTone = isActive ? "green" : isPending ? "amber" : "grey"

  return (
    <Box className={classes.card} data-selected={selected || undefined} aria-current={selected || undefined}>
      <Stack gap="md">
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Group wrap="nowrap" gap="md" miw={0}>
            <Avatar size={48} radius="md" color="brand" variant="filled" src={(supplier as any).logo || null} alt="">
              {(name.trim()[0] || "?").toUpperCase()}
            </Avatar>
            <Box miw={0}>
              <Text className={classes.name} lineClamp={1} title={name}>
                {detailHref ? (
                  <Anchor component={Link} to={detailHref} inherit className={classes.nameLink}>
                    {name}
                  </Anchor>
                ) : (
                  name
                )}
              </Text>
              <Text className={classes.code} translate="no">
                {`#${supplier.refCode ?? "-"} · ${t("order:buyerCustomer.pick.regNumber", { value: supplier.regNumber ?? "-" })}`}
              </Text>
            </Box>
          </Group>
          <DotPill tone={statusTone}>{statusLabel}</DotPill>
        </Group>

        <Box component="dl" className={classes.facts}>
          <Fact label={t("order:buyerCustomer.pick.term")} strong>{termText}</Fact>
          <Fact label={t("order:buyerCustomer.pick.discount")}>{discountText}</Fact>
          <Fact label={t("order:buyerCustomer.pick.delivery")} mono>{nextDate}</Fact>
          <Fact label={t("order:buyerCustomer.pick.promo")}>{promoText}</Fact>
          {/* The minimum is per product (variant min order); there is no order-total floor. */}
          <Fact label={t("order:buyerCustomer.pick.minOrder")}>{t("order:buyerCustomer.pick.minOrderText")}</Fact>
          <Fact label={t("order:buyerCustomer.pick.manager")}>{managerText}</Fact>
        </Box>

        <Group justify="space-between" align="center" wrap="nowrap">
          <Text className={classes.cart}>
            {t("order:buyerCustomer.pick.cart", { count: cartLines, amount: currencyFormat(cartAmount) })}
          </Text>
          <ReasonTooltip reason={catalogReason}>
            <Button color="brand" size="md" onClick={onOpenCatalog}>
              {t("order:buyerCustomer.pick.openCatalog")}
            </Button>
          </ReasonTooltip>
        </Group>
      </Stack>
    </Box>
  )
}

export function BuyerCustomerList() {
  const { t } = useTranslation(["order", "common"])
  const navigate = useNavigate()
  const cart = useSelector((state: { cart: ICart }) => state.cart.items)
  const [search, setSearch] = React.useState("")
  const [selectedId, setSelectedId] = React.useState<string>(readSupplier)

  const ORD_NET_LIST = usePermission("ORD_NET_LIST")
  const ORD_NET_DETAIL = usePermission("ORD_NET_DETAIL")
  const ORD_NET_NEW_ORDER = usePermission("ORD_NET_NEW_ORDER")

  const { data, isLoading } = useSWR(
    ORD_NET_LIST.hasAccess ? "/ord/network/list/pick" : null,
    async () => {
      const res = await NetworkApi.list({ filter: {}, offset: { page: 1, limit: 200 } })
      return (res?.rows || []) as INetworkPlus[]
    },
    { revalidateOnFocus: false },
  )
  const suppliers = React.useMemo(() => data || [], [data])

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return suppliers
    return suppliers.filter((s) =>
      [renderBusinessName(s, ""), s.profileName, s.partner?.businessName, s.regNumber, s.refCode]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    )
  }, [suppliers, search])

  const current = suppliers.find((s) => s.id === selectedId) || suppliers[0] || null

  const choose = (id: string) => {
    setSelectedId(id)
    writeSupplier(id)
  }

  const openCatalog = (id: string) => {
    writeSupplier(id)
    navigate("/buyer-sales/new")
  }

  const title = (
    <Group gap="md" wrap="wrap">
      <Title order={2}>{t("order:buyerCustomer.pick.title")}</Title>
      {suppliers.length > 0 && <SupplierSelect suppliers={suppliers} value={current?.id || ""} onChange={choose} />}
    </Group>
  )

  if (!ORD_NET_LIST.hasAccess) {
    return (
      <PageLayout title={title} noFrame>
        <DeniedCard permission="ORD_NET_LIST" />
      </PageLayout>
    )
  }

  return (
    <PageLayout
      title={title}
      subTitle={t("order:buyerCustomer.pick.description")}
      noFrame>
      <Group mb="sm" gap="md">
        <TextInput
          w={420}
          maw="100%"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          placeholder={t("order:buyerCustomer.pick.search")}
          aria-label={t("order:buyerCustomer.pick.search")}
          leftSection={<MagnifyingGlassIcon size={16} />}
        />
        <Text fz="var(--nw-fz-body)" fw={600}>
          {t("order:buyerCustomer.pick.count", { count: filtered.length })}
        </Text>
      </Group>

      <Box className={classes.layout}>
        <Stack gap="md" miw={0}>
          {isLoading ? (
            <ListState state="loading" />
          ) : suppliers.length === 0 ? (
            <ListState state="empty" title={t("order:buyerCustomer.pick.empty")} />
          ) : filtered.length === 0 ? (
            <ListState state="noResults" title={t("order:buyerCustomer.pick.noResults")} action={{ onClick: () => setSearch("") }} />
          ) : (
            filtered.map((s) => {
              const lines = cart.filter((i) => i.supplierBusinessId === s.id)
              return (
                <SupplierCard
                  key={s.id}
                  supplier={s}
                  selected={s.id === current?.id}
                  cartLines={lines.length}
                  cartAmount={lines.reduce((sum, i) => sum + i.price * i.quantity, 0)}
                  onOpenCatalog={() => openCatalog(s.id as string)}
                  detailHref={ORD_NET_DETAIL.hasAccess ? `/buyer-customer/detail/${s.id}` : undefined}
                  catalogReason={
                    !ORD_NET_NEW_ORDER.hasAccess
                      ? ORD_NET_NEW_ORDER.reason ?? undefined
                      : canOrder(s)
                        ? undefined
                        : t("order:buyerCustomer.cannotOrderInactive")
                  }
                />
              )
            })
          )}
        </Stack>

        <Box className={classes.card}>
          <Stack gap="md">
            <Text className={classes.sideTitle}>{t("order:buyerCustomer.pick.effectTitle")}</Text>
            {[1, 2, 3].map((n) => (
              <Box key={n}>
                <Text className={classes.sideItemTitle}>{t(`order:buyerCustomer.pick.effect${n}Title`)}</Text>
                <Text className={classes.sideItemText}>{t(`order:buyerCustomer.pick.effect${n}Text`)}</Text>
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>
    </PageLayout>
  )
}
